/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';

import { Button, Icon, Modal } from '@mairistem/composants';
import { request } from '@mairistem/tools';

import { URL_MENTIONS } from '@/constants';
import { useOptionsCodeAnts } from '@/hooks';
import type { CleMentionActe } from '@/types';

import MentionFormFields, { estMentionValide } from './MentionFormFields';
import MODAL_HEADER_STYLE from './modalStyles';

interface ModalAjouterMentionProps {
  open: boolean;
  /** Colonne de rattachement à l'acte, selon son type. */
  cleActe: CleMentionActe;
  identifiantActe?: number;
  onClose: () => void;
  onSaved: (mention: any) => void;
}

const ModalAjouterMention = (props: ModalAjouterMentionProps) => {
  const { open, cleActe, identifiantActe, onClose, onSaved } = props;

  const [data, setData] = React.useState<Record<string, any>>({});
  const [saving, setSaving] = React.useState(false);

  const { options: optionsCodeAnts } = useOptionsCodeAnts(open);

  React.useEffect(() => {
    if (!open) {
      setData({});
    }
  }, [open]);

  const handleChange = React.useCallback(
    (
      _event: React.SyntheticEvent | null,
      payload: { name: string; value: unknown },
    ) => {
      const { name, value } = payload;
      setData((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const handleSave = React.useCallback(() => {
    setSaving(true);
    request
      .post(URL_MENTIONS, {
        numero: Number(data.numero),
        dateApposition: data.dateApposition,
        titre: data.titre,
        texte: data.texte ?? '',
        // Seule la clé du type d'acte courant est envoyée : les autres
        // colonnes de rattachement doivent rester nulles.
        [cleActe]: identifiantActe,
        ...(!_.isNil(data.codeMentionAnts) &&
          data.codeMentionAnts !== '' && {
            codeMentionAnts: Number(data.codeMentionAnts),
          }),
      })
      .then((mention: any) => {
        onSaved(mention);
        onClose();
      })
      .catch(() => {
        toast.error(
          "Une erreur est survenue lors de l'enregistrement de la mention",
        );
      })
      .finally(() => {
        setSaving(false);
      });
  }, [data, cleActe, identifiantActe, onSaved, onClose]);

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header
        // @ts-ignore
        style={MODAL_HEADER_STYLE}
        content={"Saisie d'une mention"}
      />
      <Modal.Content>
        <MentionFormFields
          data={data}
          optionsCodeAnts={optionsCodeAnts}
          onChange={handleChange}
        />
      </Modal.Content>
      <Modal.Actions>
        <Button data-testid="mention-annuler" onClick={onClose}>
          <Icon name="close" />
          Annuler
        </Button>
        <Button
          positive
          data-testid="mention-enregistrer"
          loading={saving}
          disabled={
            !estMentionValide(data) || _.isNil(identifiantActe) || saving
          }
          onClick={handleSave}
        >
          <Icon name="check" />
          Enregistrer
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ModalAjouterMention;
